/**
 * ProfileSelector Component
 *
 * Dropdown in the top navigation that allows users to:
 * - View their currently active profile
 * - Switch between multiple company profiles
 * - See visual indicators (colors) for each profile
 * - Navigate to profile management
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */

import { memo } from 'react';
import { Building2, Plus, Settings, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useProfiles, useProfileSwitch } from '../hooks';
import { useProfileStore } from '@/stores/profileStore';
import { packsAPI } from '@/lib/api/packs';
import { toast } from 'react-hot-toast';

const ProfileSelectorComponent = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { activeProfile } = useProfileStore();
  const { data: profiles, isLoading } = useProfiles();
  const { mutateAsync: switchProfile, isPending: isSwitching } = useProfileSwitch();

  // Niche/pack labels for the switcher badges. Packs rarely change → cache hard.
  const { data: packs } = useQuery({
    queryKey: ['packs'],
    queryFn: packsAPI.list,
    staleTime: Infinity,
  });
  // Compact badge = the short niche slug; full pack title goes in the tooltip.
  const niche = (packId?: string | null) =>
    packId ? packs?.find((p) => p.id === packId) : undefined;
  const nicheLabel = (packId?: string | null): string | null =>
    packId ? niche(packId)?.niche ?? packId : null;
  const nicheTitle = (packId?: string | null): string | undefined =>
    niche(packId)?.title ?? packId ?? undefined;

  const handleProfileChange = async (profileId: string) => {
    if (profileId === 'manage') {
      navigate('/app/profiles');
      return;
    }

    if (profileId === 'create') {
      navigate('/app/company-profile/wizard');
      return;
    }

    const targetProfile = profiles?.find(p => p.id === profileId);
    const profileName = targetProfile?.profile_name || 'Profile';

    try {
      await switchProfile(profileId);
      toast.success(`Przełączono na ${profileName}`, {
        icon: '✓',
        duration: 2000,
      });
    } catch (error: unknown) {
      toast.error((error as { message?: string })?.message || 'Nie udało się przełączyć profilu');
    }
  };

  if (isLoading || !profiles || profiles.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        value={activeProfile?.id || ''}
        onValueChange={handleProfileChange}
        disabled={isSwitching}
      >
        <SelectTrigger className="h-9 w-[200px] border-muted">
          <div className="flex items-center gap-2 overflow-hidden">
            {isSwitching ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                <span className="truncate text-sm font-medium text-muted-foreground">
                  Przełączanie...
                </span>
              </>
            ) : (
              activeProfile && (
                <>
                  {/* Profile color indicator */}
                  <div
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: activeProfile.profile_color }}
                  />
                  {/* Profile name */}
                  <span className="truncate text-sm font-medium">
                    {activeProfile.profile_name}
                  </span>
                </>
              )
            )}
          </div>
        </SelectTrigger>

        <SelectContent>
          {/* List of profiles */}
          {profiles.map((profile) => (
            <SelectItem
              key={profile.id}
              value={profile.id}
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: profile.profile_color }}
                />
                <span className="truncate">{profile.profile_name}</span>
                {nicheLabel(profile.pack) && (
                  <span
                    title={nicheTitle(profile.pack)}
                    className="max-w-[110px] truncate rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                  >
                    {nicheLabel(profile.pack)}
                  </span>
                )}
                {profile.is_active && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    (Aktywny)
                  </span>
                )}
              </div>
            </SelectItem>
          ))}

          <Separator className="my-1" />

          {/* Action items */}
          <SelectItem value="create" className="font-medium text-primary">
            <div className="flex items-center gap-2">
              <Plus size={14} />
              <span>Utwórz nowy profil</span>
            </div>
          </SelectItem>

          <SelectItem value="manage" className="font-medium">
            <div className="flex items-center gap-2">
              <Settings size={14} />
              <span>Zarządzaj profilami</span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Quick info about current profile */}
      {activeProfile && (
        <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 size={14} />
          <span className="truncate max-w-[150px]">
            {activeProfile.primary_domain}
          </span>
          {nicheLabel(activeProfile.pack) && (
            <span
              title={nicheTitle(activeProfile.pack)}
              className="max-w-[120px] truncate rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium"
            >
              {nicheLabel(activeProfile.pack)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Export memoized component to prevent unnecessary re-renders
export const ProfileSelector = memo(ProfileSelectorComponent);
